// Persist per-node outputs and variable values as a workflow runs, so we can
// resume from any node without depending only on in-memory React state.
// Keyed by workflowId (one record per workflow). Safe against quota errors.

var storageQuota = require('./storageQuota');

var STORAGE_KEY_PREFIX = 'alleflow_wf_progress_';
var MAX_PAYLOAD_CHARS = 2000000; // ~2MB guardrail

function storageKey(workflowId) {
  return STORAGE_KEY_PREFIX + String(workflowId || 'default');
}

function safeGet(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

function safeSet(key, value) {
  if (value && value.length > MAX_PAYLOAD_CHARS) return false;
  // Evicts disposable data (trace, other workflows' stale progress) and
  // retries when the quota is full, instead of silently dropping the write.
  return storageQuota.safeSetItem(key, value);
}

function safeRemove(key) {
  try { localStorage.removeItem(key); } catch (e) {}
}

function readRecord(workflowId) {
  var raw = safeGet(storageKey(workflowId));
  if (!raw) return createEmptyRecord(workflowId);
  try {
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return createEmptyRecord(workflowId);
    return {
      workflowId: String(parsed.workflowId || workflowId || ''),
      startedAt: parsed.startedAt || null,
      updatedAt: parsed.updatedAt || null,
      nodeOutputById: parsed.nodeOutputById && typeof parsed.nodeOutputById === 'object' ? parsed.nodeOutputById : {},
      variableValuesByName: parsed.variableValuesByName && typeof parsed.variableValuesByName === 'object' ? parsed.variableValuesByName : {}
    };
  } catch (e) {
    return createEmptyRecord(workflowId);
  }
}

function writeRecord(record) {
  if (!record || !record.workflowId) return false;
  try {
    var json = JSON.stringify(record);
    return safeSet(storageKey(record.workflowId), json);
  } catch (e) {
    return false;
  }
}

function createEmptyRecord(workflowId) {
  return {
    workflowId: String(workflowId || ''),
    startedAt: null,
    updatedAt: null,
    nodeOutputById: {},
    variableValuesByName: {}
  };
}

function beginRun(workflowId) {
  if (!workflowId) return;
  var record = createEmptyRecord(workflowId);
  record.startedAt = Date.now();
  record.updatedAt = record.startedAt;
  writeRecord(record);
}

function recordNodeOutput(workflowId, nodeId, output) {
  if (!workflowId || !nodeId) return;
  var record = readRecord(workflowId);
  record.nodeOutputById[String(nodeId)] = {
    output: output === undefined ? null : output,
    completedAt: Date.now()
  };
  record.updatedAt = Date.now();
  writeRecord(record);
}

function recordVariableValue(workflowId, name, value) {
  if (!workflowId || !name) return;
  var record = readRecord(workflowId);
  record.variableValuesByName[String(name)] = {
    value: value === undefined ? null : value,
    updatedAt: Date.now()
  };
  record.updatedAt = Date.now();
  writeRecord(record);
}

function clearProgress(workflowId) {
  if (!workflowId) return;
  safeRemove(storageKey(workflowId));
}

// Return the stored outputs mapped by node TITLE (needed by the backend)
// plus a plain variable map. Includes everything recorded up to and including
// the resume node's completion (if known). We don't drop "future" entries —
// the set-once + beginRun() reset policy already keeps the record clean, and
// dropping entries can hide upstream outputs whose clocks drift slightly.
function buildResumeSnapshot(workflowId, workflowState, resumeNodeId) {
  var record = readRecord(workflowId);
  var priorNodeOutputByName = {};
  var priorVariableValuesByName = {};
  var nodes = (workflowState && Array.isArray(workflowState.nodes)) ? workflowState.nodes : [];
  var resumeKey = String(resumeNodeId || '');
  var nodesIncluded = [];

  nodes.forEach(function(node) {
    if (!node || !node.id) return;
    var nid = String(node.id);
    var entry = record.nodeOutputById[nid];
    if (!entry) return;
    var title = String(node.title || '').trim();
    if (!title) return;
    priorNodeOutputByName[title] = entry.output;
    nodesIncluded.push(nid + ':' + title);
  });

  var varNames = Object.keys(record.variableValuesByName);
  for (var i = 0; i < varNames.length; i++) {
    var v = record.variableValuesByName[varNames[i]];
    priorVariableValuesByName[varNames[i]] = v ? v.value : null;
  }

  if (typeof console !== 'undefined' && console.log) {
    console.log('[WFProgress] buildResumeSnapshot wf=' + workflowId
      + ' resumeNode=' + resumeKey
      + ' outputs=' + nodesIncluded.length
      + ' vars=' + varNames.length
      + ' storedIds=' + JSON.stringify(Object.keys(record.nodeOutputById)));
  }

  return {
    priorNodeOutputByName: priorNodeOutputByName,
    priorVariableValuesByName: priorVariableValuesByName,
    updatedAt: record.updatedAt
  };
}

module.exports = {
  beginRun: beginRun,
  recordNodeOutput: recordNodeOutput,
  recordVariableValue: recordVariableValue,
  clearProgress: clearProgress,
  buildResumeSnapshot: buildResumeSnapshot,
  readRecord: readRecord
};
